import Decimal from 'decimal.js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../../lib/authStore.js';
import { effectiveRateFor, formatRate, loadExchangeRates, type RateRow } from '../../lib/exchangeRates.js';
import { formatMoney } from '../../lib/money.js';

// Shared across every form on the page; refreshed after a minute so a rate
// added on the rates page shows up without a reload.
let cache: { at: number; rates: Promise<RateRow[]> } | null = null;
function rates(): Promise<RateRow[]> {
  if (!cache || Date.now() - cache.at > 60_000) cache = { at: Date.now(), rates: loadExchangeRates() };
  return cache.rates;
}

/**
 * What a foreign-currency amount will post as in the base currency, and at
 * which rate — shown BEFORE saving, under a voucher's or an expense's amount.
 *
 * The server books every line in base at the rate in force on the document's
 * date; without a rate it refuses (currency.rate_not_found). Saying so up front,
 * with a way to the rates page, beats a generic "could not save". Nothing is
 * rendered for a base-currency amount.
 */
export function FxEquivalent({ amount, currency, date, className }: {
  amount: string;
  currency: string;
  /** The document's date (YYYY-MM-DD) — the rate in force then is the one used. */
  date: string;
  className?: string;
}): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const base = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? '';
  const [loaded, setRates] = useState<RateRow[] | null>(null);
  useEffect(() => {
    if (!base || currency === base) return;
    let on = true;
    void rates().then((r) => { if (on) setRates(r); }).catch(() => { cache = null; });
    return () => { on = false; };
  }, [base, currency]);

  if (!base || !currency || currency === base || !loaded) return null;
  const rate = effectiveRateFor(loaded, currency, base, date || new Date().toISOString().slice(0, 10));
  if (!rate) {
    return (
      <p className={`text-xs text-warning ${className ?? ''}`} role="status">
        {t('fx.noRate', { from: currency, to: base })}{' '}
        <Link to="/accounting/exchange-rates" className="underline">{t('fx.addRate')}</Link>
      </p>
    );
  }
  let value: Decimal;
  try { value = new Decimal(amount || '0').times(rate); } catch { return null; }
  return (
    <p className={`text-xs text-fg-muted ${className ?? ''}`} dir="ltr">
      ≈ {formatMoney(value.toFixed(4), { locale: i18n.language, currency: base })}
      {' · '}1 {currency} = {formatRate(rate, i18n.language)} {base}
    </p>
  );
}
